import Space from '@rebass/space'
import { graphql, Link } from 'gatsby'
import Container from '../components/Container'
import { Flex, Box } from '@rebass/grid'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React from 'react'
import styled from 'styled-components'
import Card from '../components/Card'
import Badge from '../components/Badge'
import SEO from '../components/SEO'
import { ThemeConsumer } from 'styled-components'
import LogoUI8 from '../components/logos/LogoUI8'
import LogoSmashicons from '../components/logos/LogoSmashicons'
import LogoGumroad from '../components/logos/LogoGumroad'
import LogoCraftwork from '../components/logos/LogoCraftwork'
import LogoVectopus from '../components/logos/LogoVectopus'

let thumbnailsArray = []

const StyledMenuBar = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  background-color: ${(props) =>
    props.theme.appearance === 'light' ? '#fafafa' : 'rgba(0,0,0, .15)'};
  height: 24px;
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.15);
  mask-image: linear-gradient(to left, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 0));
`

const FeaturedIcons = ({ props, data }) => {
  data.thumbnail.edges.map((edge, index) => {
    thumbnailsArray[edge.node.relativeDirectory] = [edge.node]
  })

  return (
    <ThemeConsumer>
      {(theme) => (
        <>
          <SEO
            title="Free & Premium Icon Sets • Curated Icon Resources for Designers"
            description="Discover our handpicked collection of free and premium icon sets from top designers. Download high-quality icons for your projects with IconJar - the ultimate icon organizer for macOS."
            meta={[
              {
                name: 'keywords',
                content:
                  'free icons, premium icons, icon sets, design resources, UI icons, web icons, mobile icons, icon library, IconJar, designer tools',
              },
              {
                property: 'og:url',
                content: 'https://geticonjar.com/icon-sets',
              },
              {
                name: 'twitter:site',
                content: '@geticonjar',
              },
            ]}
          />

          {/* Structured Data */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'CollectionPage',
                name: 'Free & Premium Icon Sets',
                alternateName: 'IconJar Resources - Curated Icon Collections',
                description:
                  'Curated collection of high-quality icon sets from top designers worldwide. Discover free and premium icon libraries for designers and developers.',
                url: 'https://geticonjar.com/icon-sets',
                publisher: {
                  '@type': 'Organization',
                  name: 'IconJar',
                  url: 'https://geticonjar.com',
                },
                creator: {
                  '@type': 'Person',
                  name: 'Davey Heuser',
                  url: 'https://twitter.com/daveyheuser',
                },
                keywords: [
                  'free icons',
                  'premium icons',
                  'icon sets',
                  'design resources',
                  'UI icons',
                  'web icons',
                  'mobile icons',
                  'icon library',
                  'designer tools',
                  'icon collections',
                ],
                audience: {
                  '@type': 'Audience',
                  audienceType:
                    'Designers, Developers, Creative Professionals, UI/UX Designers',
                },
                mainEntity: {
                  '@type': 'ItemList',
                  name: 'Icon Sets Collection',
                  description:
                    'Collection of free and premium icon sets for designers and developers',
                  numberOfItems: data.jsonData.edges.length,
                  itemListElement: data.jsonData.edges.map((edge, index) => ({
                    '@type': 'CreativeWork',
                    position: index + 1,
                    name: edge.node.name,
                    description: edge.node.description,
                    category: edge.node.category,
                    author: {
                      '@type': 'Person',
                      name: edge.node.author.name,
                      url: edge.node.author.website,
                    },
                    url: `https://geticonjar.com/icon-sets/${edge.node.slug}`,
                    keywords: [
                      `${edge.node.category} icons`,
                      edge.node.name,
                      'icon set',
                      'design resources',
                      'graphic design',
                      'UI design',
                    ],
                    license:
                      edge.node.category === 'free'
                        ? 'Free License'
                        : 'Premium License',
                    numberOfIcons: edge.node.numberOfIcons,
                    fileFormat: ['SVG', 'PNG', 'IconJar'],
                    usageType: 'Commercial and Personal Use',
                  })),
                },
                breadcrumb: {
                  '@type': 'BreadcrumbList',
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      position: 1,
                      name: 'Home',
                      item: 'https://geticonjar.com',
                    },
                    {
                      '@type': 'ListItem',
                      position: 2,
                      name: 'Icon Sets',
                      item: 'https://geticonjar.com/icon-sets',
                    },
                  ],
                },
                potentialAction: {
                  '@type': 'SearchAction',
                  target: {
                    '@type': 'EntryPoint',
                    urlTemplate:
                      'https://geticonjar.com/icon-sets?search={search_term_string}',
                  },
                  'query-input': 'required name=search_term_string',
                },
              }),
            }}
          />

          {/* Hero */}
          <Container my={[4, 5]}>
            <Box textAlign={'center'}>
              <h1>Free & premium icon sets</h1>
              <p className="subtitle">
                Curated collection of high-quality icon sets from top designers
                worldwide
              </p>
            </Box>
          </Container>

          <Container my={[4, 5]}>
            <Flex flexWrap="wrap" mb={4}>
              {data.jsonData.edges
                .map((iconSet, index) => {
                  const items = [
                    <Box
                      width={[1, 1 / 2, 1 / 3]}
                      px={[0, 3, 3]}
                      mb={[3, 4]}
                      key={index}>
                      <Box
                        as={Link}
                        to={'/icon-sets/' + iconSet.node.slug}
                        style={{ textDecoration: 'none' }}>
                        <Card subtle animateonhover="true">
                          <Box style={{ position: 'relative' }}>
                            <GatsbyImage
                              image={getImage(
                                thumbnailsArray[iconSet.node.slug][0],
                              )}
                              alt={`${iconSet.node.name} icon set preview - ${iconSet.node.numberOfIcons} ${iconSet.node.category} icons by ${iconSet.node.author.name}`}
                            />
                            <Box
                              style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                              }}>
                              <Badge colorscheme="white">
                                {iconSet.node.category === 'free'
                                  ? 'Free'
                                  : 'Premium'}
                              </Badge>
                            </Box>
                          </Box>
                          <Space p={3}>
                            <div>
                              <h4>{iconSet.node.name}</h4>
                              <p style={{ marginBottom: '0' }}>
                                {iconSet.node.numberOfIcons} icons by{' '}
                                {iconSet.node.author.name}
                              </p>
                            </div>
                          </Space>
                        </Card>
                      </Box>
                    </Box>,
                  ]

                  // Insert first 3 "We're supported by" cards after 6 items (2 rows)
                  if (index === 5) {
                    items.push(
                      <Box width={1} key="supported-by-section-1">
                        <Flex flexWrap="wrap" alignItems="center" mx={2}>
                          <Box width={[1, 1 / 2, 1 / 3]} mb={[3, 4]}>
                            <a href="https://www.ui8.net/?rel=icnjr">
                              <Card
                                as={Flex}
                                alignItems="center"
                                justifyContent="center"
                                style={{
                                  height: '133px',
                                  backgroundColor: '#2B292B',
                                }}
                                mx={2}
                                animateonhover="true">
                                <LogoUI8 width="48" height="48" />
                              </Card>
                            </a>
                          </Box>
                          <Box width={[1, 1 / 2, 1 / 3]} mb={[3, 4]}>
                            <a href="https://iconfinder.com">
                              <Card
                                as={Flex}
                                alignItems="center"
                                justifyContent="center"
                                style={{
                                  height: '133px',
                                  backgroundColor: '#14984D',
                                }}
                                mx={2}
                                animateonhover="true">
                                <GatsbyImage
                                  image={getImage(data.logoIconfinder)}
                                  alt="Iconfinder - Premium icon marketplace and design resources"
                                />
                              </Card>
                            </a>
                          </Box>
                          <Box width={[1, 1 / 2, 1 / 3]} mb={[3, 4]}>
                            <a href="https://smashicons.com">
                              <Card
                                as={Flex}
                                alignItems="center"
                                justifyContent="center"
                                style={{
                                  height: '133px',
                                  backgroundColor: '#2a2d2d',
                                }}
                                mx={2}
                                animateonhover="true">
                                <LogoSmashicons width="64" height="64" />
                              </Card>
                            </a>
                          </Box>
                        </Flex>
                      </Box>,
                    )
                  }

                  // Insert last 3 "We're supported by" cards after 12 items (4 rows)
                  if (index === 11) {
                    items.push(
                      <Box width={1} key="supported-by-section-2">
                        <Flex flexWrap="wrap" alignItems="center" mx={2}>
                          <Box width={[1, 1 / 2, 1 / 3]} mb={[3, 4]}>
                            <a href="https://discover.gumroad.com/design/icons?a=581296979">
                              <Card
                                as={Flex}
                                alignItems="center"
                                justifyContent="center"
                                style={{
                                  height: '133px',
                                  backgroundColor: '#FFC900',
                                }}
                                mx={2}
                                animateonhover="true">
                                <LogoGumroad width="128" />
                              </Card>
                            </a>
                          </Box>
                          <Box width={[1, 1 / 2, 1 / 3]} mb={[3, 4]}>
                            <a href="https://craftwork.design/?ref=333">
                              <Card
                                as={Flex}
                                alignItems="center"
                                justifyContent="center"
                                style={{
                                  height: '133px',
                                  backgroundColor: '#ececec',
                                }}
                                mx={2}
                                animateonhover="true">
                                <LogoCraftwork width="128" />
                              </Card>
                            </a>
                          </Box>
                          <Box width={[1, 1 / 2, 1 / 3]} mb={[3, 4]}>
                            <a href="https://vectopus.com">
                              <Card
                                as={Flex}
                                alignItems="center"
                                justifyContent="center"
                                style={{
                                  height: '133px',
                                  backgroundColor: '#ffd00f',
                                }}
                                mx={2}
                                animateonhover="true">
                                <LogoVectopus width="128" />
                              </Card>
                            </a>
                          </Box>
                        </Flex>
                      </Box>,
                    )
                  }

                  return items
                })
                .flat()}
            </Flex>
          </Container>
        </>
      )}
    </ThemeConsumer>
  )
}

export default FeaturedIcons

export const query = graphql`
  query {
    jsonData: allFeaturedIconsJson(sort: { setId: DESC }) {
      edges {
        node {
          name
          slug
          category
          numberOfIcons
          author {
            name
          }
        }
      }
    }
    thumbnail: allFile(
      filter: { name: { eq: "thumbnail@2x" } }
      sort: { relativeDirectory: ASC }
    ) {
      edges {
        node {
          childImageSharp {
            gatsbyImageData(
              width: 710
              quality: 100
              placeholder: NONE
              layout: CONSTRAINED
              formats: [AUTO, WEBP]
            )
          }
          relativeDirectory
        }
      }
    }
    sketchPluginIcon: file(relativePath: { eq: "sketchplugin@2x.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 64
          quality: 100
          placeholder: NONE
          formats: [AUTO, WEBP]
        )
      }
    }
    sketchPluginMenu: file(relativePath: { eq: "sketchplugin-menu@2x.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 248
          quality: 80
          placeholder: NONE
          formats: [AUTO, WEBP]
        )
      }
    }
    sketchPluginMenuDark: file(
      relativePath: { eq: "sketchplugin-menu-dark@2x.png" }
    ) {
      childImageSharp {
        gatsbyImageData(
          width: 248
          quality: 80
          placeholder: NONE
          formats: [AUTO, WEBP]
        )
      }
    }
    logoIconfinder: file(relativePath: { eq: "logo-iconfinder@2x.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 151
          quality: 100
          placeholder: NONE
          formats: [AUTO, WEBP]
        )
      }
    }
  }
`
